import type { AiAllowanceSummary, AiUsageSummary } from '~/types/models';

type AiUsageOptions = {
    auto?: boolean;
};

function useAiUsageRequest<T extends AiAllowanceSummary>(
    scope: 'admin' | 'allowance',
    allowedRoles: string[],
    options: AiUsageOptions = {},
) {
    const tenantStore = useTenantStore();
    const { $auth } = useNuxtApp();
    const usage = ref<T | null>(null);
    const loading = ref(false);
    const error = ref('');

    const canView = computed(() => (
        allowedRoles.includes(tenantStore.currentTenantRole || '')
    ));

    function setUsage(summary: T) {
        const current = usage.value;
        if (
            current
            && current.resetsAt === summary.resetsAt
            && current.monthlyUsed > summary.monthlyUsed
        ) {
            return;
        }
        usage.value = summary;
    }

    async function refresh(): Promise<T | null> {
        const tenantId = tenantStore.currentTenantId;
        if (!tenantId || !canView.value) {
            usage.value = null;
            error.value = '';
            return null;
        }

        loading.value = true;
        error.value = '';
        try {
            const token = await $auth?.currentUser?.getIdToken();
            if (!token) throw new Error('Your session has expired. Please sign in again.');

            const summary = await $fetch<T>('/api/ai/usage', {
                headers: { authorization: `Bearer ${token}` },
                query: { tenantId, scope },
            });
            setUsage(summary);
            return summary;
        } catch (usageError: any) {
            error.value = usageError?.data?.statusMessage
                || usageError?.message
                || 'AI usage is currently unavailable.';
            console.warn('Unable to load AI usage summary:', usageError);
            return null;
        } finally {
            loading.value = false;
        }
    }

    watch(
        () => [tenantStore.currentTenantId, canView.value] as const,
        ([tenantId, allowed]) => {
            if (!tenantId || !allowed) {
                usage.value = null;
                error.value = '';
                return;
            }

            if (options.auto !== false) void refresh();
        },
        { immediate: true },
    );

    return {
        usage,
        loading,
        error,
        canView,
        refresh,
        setUsage,
    };
}

export function useAiUsageSummary(options: AiUsageOptions = {}) {
    return useAiUsageRequest<AiUsageSummary>('admin', ['owner', 'admin'], options);
}

export function useAiAllowanceSummary(options: AiUsageOptions = {}) {
    return useAiUsageRequest<AiAllowanceSummary>('allowance', ['owner', 'admin', 'member'], options);
}
